const DEFAULT_MAX_WIDTH = 1200;
const DEFAULT_TARGET_KB = 400;
const MIN_QUALITY = 0.6;
const INITIAL_QUALITY = 0.82;
const QUALITY_STEP = 0.06;
const WIDTH_STEPS = [1200, 1080, 960, 840, 720];

const loadImage = (file) => new Promise((resolve, reject) => {
  const image = new Image();
  const objectUrl = URL.createObjectURL(file);

  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(image);
  };

  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('Failed to read image file'));
  };

  image.src = objectUrl;
});

const canvasToBlob = (canvas, mimeType, quality) => new Promise((resolve) => {
  canvas.toBlob((blob) => resolve(blob), mimeType, quality);
});

const getOutputExtension = (mimeType) => {
  if (mimeType === 'image/webp') return 'webp';
  if (mimeType === 'image/png') return 'png';
  return 'jpg';
};

const buildOutputName = (file, mimeType) => {
  const baseName = (file?.name || 'image').replace(/\.[^.]+$/, '');
  return `${baseName}_1200w.${getOutputExtension(mimeType)}`;
};

export const compressImageFile = async (file, options = {}) => {
  if (!file) {
    throw new Error('Missing image file');
  }

  const maxWidth = options.maxWidth || DEFAULT_MAX_WIDTH;
  const hasTargetSize = Number.isFinite(options.targetSizeKB) && options.targetSizeKB > 0;
  const targetSizeKB = hasTargetSize ? options.targetSizeKB : DEFAULT_TARGET_KB;
  const targetSizeBytes = hasTargetSize ? targetSizeKB * 1024 : null;
  const qualityStart = options.initialQuality || INITIAL_QUALITY;
  const qualityStep = options.qualityStep || QUALITY_STEP;
  const minQuality = options.minQuality || MIN_QUALITY;
  const preferredMimeTypes = options.preferredMimeTypes || ['image/webp', 'image/jpeg'];
  const allowDownscale = options.allowDownscale !== false;

  const image = await loadImage(file);
  const naturalWidth = image.naturalWidth || image.width;
  const naturalHeight = image.naturalHeight || image.height;

  if (!naturalWidth || !naturalHeight) {
    throw new Error('Unable to determine image dimensions');
  }

  const widthSteps = Array.isArray(options.widthSteps) && options.widthSteps.length > 0
    ? options.widthSteps
    : WIDTH_STEPS.filter((width) => width <= maxWidth);

  const candidateWidths = allowDownscale
    ? [...new Set([maxWidth, ...widthSteps])]
        .filter((width) => width > 0)
        .sort((a, b) => b - a)
    : [naturalWidth];

  let bestBlob = null;
  let bestMimeType = preferredMimeTypes[0];
  let bestWidth = naturalWidth;
  let bestQuality = qualityStart;

  for (const widthLimit of candidateWidths) {
    const outputWidth = Math.min(widthLimit, naturalWidth);
    const outputHeight = Math.round((naturalHeight * outputWidth) / naturalWidth);

    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas is not available for image compression');
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(image, 0, 0, outputWidth, outputHeight);

    for (const mimeType of preferredMimeTypes) {
      for (let quality = qualityStart; quality >= minQuality; quality -= qualityStep) {
        const blob = await canvasToBlob(canvas, mimeType, quality);
        if (!blob) continue;

        bestBlob = blob;
        bestMimeType = mimeType;
        bestWidth = outputWidth;
        bestQuality = quality;

        if (!hasTargetSize || blob.size <= targetSizeBytes) {
          return {
            file: new File([blob], buildOutputName(file, mimeType), { type: mimeType }),
            blob,
            mimeType,
            width: outputWidth,
            height: outputHeight,
            quality,
            originalSizeBytes: file.size,
            compressedSizeBytes: blob.size,
          };
        }
      }
    }
  }

  if (!bestBlob) {
    throw new Error('Failed to compress image');
  }

  return {
    file: new File([bestBlob], buildOutputName(file, bestMimeType), { type: bestMimeType }),
    blob: bestBlob,
    mimeType: bestMimeType,
    width: bestWidth,
    height: Math.round((naturalHeight * bestWidth) / naturalWidth),
    quality: bestQuality,
    originalSizeBytes: file.size,
    compressedSizeBytes: bestBlob.size,
  };
};
